// Nomad-mon Go! — FORGED CARDS: DUOS + FUSIONS (client logic, pure).
//
// data/fusions.json (generated by scripts/gen-forged.mjs) is the single data
// file the client, the engine and the server share. The SERVER is the
// authority for ownership and spending; this module mirrors its requirement
// validator exactly so the UI can say "ready" honestly, auto-pick reagents and
// run the guest sandbox locally. No DOM here.

export const TYPE_ORDER = ['IRON', 'VOYAGE', 'HUSTLE', 'MYSTIC', 'CHAOS', 'HEART'];
const RANK = { common: 0, rare: 1, epic: 2, legendary: 3, mythic: 4, eternal: 5 };
export const rarityRank = (r) => RANK[r] ?? 0;
const STARTER = new Set(['rocket-snack', '16-2-vs-15-1-the-mechanical-honesty-war']);

let DATA = { duos: [], fusions: [], dupeXp: {} };
const byForgedId = new Map();

export async function loadForged(bust) {
  try {
    const r = await fetch(`./data/fusions.json?${bust}`);
    if (!r.ok) throw new Error(`fusions.json ${r.status}`);
    setForgedData(await r.json());
  } catch (err) {
    console.warn('[nomadmon] forged data unavailable:', err && err.message);
  }
  return DATA;
}
export function setForgedData(d) {
  DATA = { duos: d.duos || [], fusions: d.fusions || [], dupeXp: d.dupeXp || {} };
  byForgedId.clear();
  for (const x of [...DATA.duos, ...DATA.fusions]) byForgedId.set(x.id, x);
}
export const duos = () => DATA.duos;
export const fusions = () => DATA.fusions;
export const recipe = (id) => byForgedId.get(id) || null;
export const forgedCardDefs = () => [...DATA.duos, ...DATA.fusions].map(x => x.card);
export const isForged = (d) => !!(d && d.forged);
export function dupeXpFor(rarity) {
  return DATA.dupeXp[rarity] ?? ({ common: 150, rare: 200, epic: 260, legendary: 340, mythic: 450, eternal: 600 })[rarity] ?? 150;
}

// ---------------------------------------------------------------- reagents
export function reagentEligible(c) {
  return !!c && !c.forged && c.role !== 'support' && !STARTER.has(c.id);
}

// Mirrors the server validator term for term. `cards` are card defs.
export function checkReq(req, cards) {
  const problems = [];
  const ids = cards.map(c => c.id);
  if (new Set(ids).size !== ids.length) problems.push('duplicate card');
  if (cards.length !== req.n) problems.push(`needs exactly ${req.n} cards`);
  const terms = reqTerms(req, cards);
  for (const t of terms) if (!t.ok) problems.push(t.label);
  if (cards.some(c => !reagentEligible(c))) problems.push('ineligible card');
  return { ok: problems.length === 0, problems, terms };
}

// The recipe as checkable terms with have/need — drives the locked tile's
// requirement chips and the confirm screen.
export function reqTerms(req, cards) {
  const terms = [];
  const allowed = req.rarities && req.rarities.length ? new Set(req.rarities) : null;
  const pool = allowed ? cards.filter(c => allowed.has(c.rarity)) : cards;
  const noun = allowed ? (req.rarities.length === 1 ? `${req.rarities[0]}s` : `${req.rarities.join('/')}`) : 'cards';
  terms.push({ key: 'n', label: `${req.n} ${noun}`, have: Math.min(pool.length, req.n), need: req.n, ok: pool.length >= req.n && (!allowed || cards.every(c => allowed.has(c.rarity))) });
  const tcount = {};
  for (const c of cards) tcount[c.type] = (tcount[c.type] || 0) + 1;
  if (req.perType) {
    const have = TYPE_ORDER.filter(t => (tcount[t] || 0) >= req.perType).length;
    terms.push({ key: 'perType', label: `${req.perType} of every type`, have, need: 6, ok: have === 6 });
  }
  if (req.minTypes) {
    const have = Object.keys(tcount).length;
    terms.push({ key: 'minTypes', label: req.minTypes === 6 ? 'one of each type' : `${req.minTypes} types`, have: Math.min(have, req.minTypes), need: req.minTypes, ok: have >= req.minTypes });
  }
  for (const [t, k] of Object.entries(req.types || {})) {
    const have = tcount[t] || 0;
    terms.push({ key: `type:${t}`, label: `${k} ${t.charAt(0)}${t.slice(1).toLowerCase()}`, type: t, have: Math.min(have, k), need: k, ok: have >= k });
  }
  if (req.samePerson) {
    const pc = {};
    for (const c of cards) if (c.person) pc[c.person] = (pc[c.person] || 0) + 1;
    const have = Math.max(0, ...Object.values(pc));
    terms.push({ key: 'samePerson', label: `${req.samePerson} of one nomad`, have: Math.min(have, req.samePerson), need: req.samePerson, ok: have >= req.samePerson });
  }
  for (const [r, k] of Object.entries(req.atLeast || {})) {
    const have = cards.filter(c => rarityRank(c.rarity) >= rarityRank(r)).length;
    terms.push({ key: `atLeast:${r}`, label: `${k} ${r}+`, have: Math.min(have, k), need: k, ok: have >= k });
  }
  return terms;
}

// What a reagent is "worth" to the player — lowest goes first. A spare copy
// costs (almost) nothing, then rarity, then level, then power.
export function reagentValue(c, level = 1, count = 1, pwrOf = () => 0) {
  return (count > 1 ? 0 : 100000) + rarityRank(c.rarity) * 10000 + level * 300 + pwrOf(c);
}

// Greedy constrained pick of the cheapest reagent set that satisfies `req`.
// `cands` = eligible OWNED card defs sorted cheapest-first. Returns ids or null.
export function autoSelect(req, cands) {
  const allowed = req.rarities && req.rarities.length ? new Set(req.rarities) : null;
  const pool = cands.filter(c => reagentEligible(c) && (!allowed || allowed.has(c.rarity)));
  if (pool.length < req.n) return null;
  const tryWith = (personFirst) => {
    const pick = [];
    const has = (c) => pick.includes(c);
    const take = (pred, k) => {
      let got = pick.filter(pred).length;
      for (const c of pool) { if (got >= k) break; if (!has(c) && pred(c)) { pick.push(c); got++; } }
      return got >= k;
    };
    if (req.samePerson && personFirst && !take(c => c.person === personFirst, req.samePerson)) return null;
    // rarity floors first (expensive cards also count toward types)
    const floors = Object.entries(req.atLeast || {}).sort((a, b) => rarityRank(b[0]) - rarityRank(a[0]));
    for (const [r, k] of floors) if (!take(c => rarityRank(c.rarity) >= rarityRank(r), k)) return null;
    for (const [t, k] of Object.entries(req.types || {})) if (!take(c => c.type === t, k)) return null;
    if (req.perType) for (const t of TYPE_ORDER) if (!take(c => c.type === t, req.perType)) return null;
    if (req.minTypes) {
      while (new Set(pick.map(c => c.type)).size < req.minTypes) {
        const have = new Set(pick.map(c => c.type));
        const next = pool.find(c => !has(c) && !have.has(c.type));
        if (!next) return null;
        pick.push(next);
      }
    }
    for (const c of pool) { if (pick.length >= req.n) break; if (!has(c)) pick.push(c); }
    if (pick.length !== req.n) return null;
    return checkReq(req, pick).ok ? pick.map(c => c.id) : null;
  };
  if (!req.samePerson) return tryWith(null);
  // try nomads in order of how cheaply they can fill the person term
  const byP = {};
  for (const c of pool) if (c.person) (byP[c.person] ||= []).push(c);
  const people = Object.entries(byP).filter(([, l]) => l.length >= req.samePerson)
    .sort((a, b) => pool.indexOf(a[1][req.samePerson - 1]) - pool.indexOf(b[1][req.samePerson - 1]));
  for (const [p] of people) { const r = tryWith(p); if (r) return r; }
  return null;
}

// The minted card inherits the AVERAGE XP of what was spent (spare copies
// count as fresh level-1 copies; a last copy brings its own XP).
export function mintXp(contribs, maxXp = 18430) {
  if (!contribs.length) return 0;
  return Math.min(maxXp, Math.round(contribs.reduce((a, b) => a + b, 0) / contribs.length));
}
